import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError } from "../../utils/toDisplayString";

export const resourcePersonBankInitialState = {
  bankDetails: null,
  bankDetailsLoading: false,
  bankDetailsError: null,
  bankMutationLoading: false,
  bankMutationError: null,
  banks: [],
  banksLoading: false,
  banksError: null,
};

function buildUrl(path, params = {}) {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")).toString();
  return `${API_URL.replace(/\/$/, "")}${path}${query ? `?${query}` : ""}`;
}

async function request(path, { method = "GET", params, body } = {}) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const token = authSession.getToken();
  const response = await safeFetch(buildUrl(path, params), {
    method,
    headers: { Accept: "application/json", ...(body !== undefined ? { "Content-Type": "application/json" } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: "omit",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const text = await response.text();
  let data = {};
  if (text) {
    try { data = JSON.parse(text); } catch { throw new Error(`Bank details API returned a non-JSON response (${response.status}).`); }
  }
  if (!response.ok || data.error || data.success === false) {
    const error = new Error(
      explainApiError(
        response.status,
        data || {},
        `Request failed with status ${response.status}.`
      )
    );
    error.status = response.status;
    throw error;
  }
  return data?.data && typeof data.data === "object" ? data.data : data;
}

/**
 * resourcePersonBankStore — payout bank details slice.
 *
 *   GET   /api/resource-person/bank-details
 *   PATCH /api/resource-person/bank-details
 *   GET   /api/resource-person/banks
 */
export function createResourcePersonBankActions(set) {
  const mutate = (method) => async (payload) => {
    set({ bankMutationLoading: true, bankMutationError: null });
    try {
      const details = await request("/api/resource-person/bank-details", { method, body: payload });
      set({ bankMutationLoading: false, bankDetails: payload === null ? null : details });
      return { success: true, bankDetails: details };
    } catch (error) {
      set({ bankMutationLoading: false, bankMutationError: error.message });
      return { success: false, error: error.message };
    }
  };

  return {
    fetchBankDetails: async () => {
      set({ bankDetailsLoading: true, bankDetailsError: null });
      try {
        const details = await request("/api/resource-person/bank-details");
        set({ bankDetailsLoading: false, bankDetails: details });
        return { success: true, bankDetails: details };
      } catch (error) {
        // 404 is the contract's "no bank details saved yet" state, not a
        // failure. Render the empty state instead of an error banner.
        if (error.status === 404) {
          set({ bankDetailsLoading: false, bankDetailsError: null, bankDetails: null });
          return { success: true, bankDetails: null };
        }
        set({ bankDetailsLoading: false, bankDetailsError: error.message });
        return { success: false, error: error.message };
      }
    },
    createBankDetails: mutate("PATCH"),
    updateBankDetails: mutate("PATCH"),
    fetchBanks: async (params = {}) => {
      set({ banksLoading: true, banksError: null });
      try {
        // GET /api/resource-person/banks
        const data = await request("/api/resource-person/banks", { params });
        const banks = Array.isArray(data) ? data : data?.items || data?.banks || [];
        set({ banksLoading: false, banks });
        return { success: true, banks };
      } catch (error) {
        set({ banksLoading: false, banksError: error.message });
        return { success: false, error: error.message };
      }
    },
  };
}
